import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS, BOOK_URL } from "@/lib/site";

const LineMask = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const valveY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const valveScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden"
    >
      {/* ambient glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[70vh] w-[70vh] rounded-full bg-[#00E5FF]/10 blur-[120px]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col px-6 lg:px-10 pt-24 pb-16"
      >
        {/* top row: brand + location meta */}
        <div className="flex items-start justify-between">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="max-w-[300px]"
          >
            <img
              src={ASSETS.logoHorizontal}
              alt="Leak SmartShield by Caroma"
              data-testid="hero-brand-logo"
              className="h-16 sm:h-20 w-auto object-contain [filter:brightness(0)_invert(1)]"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="hidden md:block font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 text-right"
          >
            Perth · Australia
            <br /> 3000+ installs
          </motion.p>
        </div>

        {/* Valve — centered, large, above the headline */}
        <motion.div
          style={{ y: valveY, scale: valveScale }}
          className="relative flex justify-center items-center mt-4 sm:mt-2"
        >
          {/* pulsing blue light illuminating from behind */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 h-[76vw] w-[76vw] max-h-[880px] max-w-[880px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,150,255,0.6) 0%, rgba(0,120,255,0.28) 35%, rgba(0,80,200,0) 70%)",
              filter: "blur(60px)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.55, 0.95, 0.55], scale: [1, 1.12, 1] }}
            transition={{
              opacity: { duration: 5.5, repeat: Infinity, ease: "easeInOut" },
              scale: { duration: 5.5, repeat: Infinity, ease: "easeInOut" },
            }}
          />
          {/* top-edge blue rim glow (offset up-left because unit is tilted -20°) */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-[26%] -translate-x-[38%] -translate-y-1/2 h-[24vw] w-[54vw] max-h-[260px] max-w-[720px] rounded-full"
            style={{
              background:
                "radial-gradient(ellipse, rgba(22,158,217,0.55) 0%, rgba(22,158,217,0.18) 45%, rgba(22,158,217,0) 75%)",
              filter: "blur(28px)",
              transform: "translate(-38%, -50%) rotate(-20deg)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.55, 0.9, 0.55] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* the valve, gently floating with a fixed ~20° left tilt */}
          <motion.img
            src={ASSETS.phynUnit}
            alt="Leak SmartShield smart water valve by Caroma"
            data-testid="hero-valve-image"
            initial={{ opacity: 0, scale: 1.08, rotate: -20 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: [0, -14, 0],
              rotate: [-20, -18.5, -20, -21.5, -20],
            }}
            transition={{
              opacity: { duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 },
              scale: { duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 },
              y: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 },
              rotate: { duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 },
            }}
            className="relative w-[135vw] sm:w-[108vw] lg:w-[99vw] max-w-[1512px] max-h-[75vh] h-auto object-contain select-none"
            style={{
              maskImage:
                "radial-gradient(ellipse 62% 62% at 50% 50%, #000 55%, transparent 85%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 62% 62% at 50% 50%, #000 55%, transparent 85%)",
              filter:
                "drop-shadow(0 -14px 32px rgba(22,158,217,0.55)) drop-shadow(0 -4px 14px rgba(22,158,217,0.4))",
            }}
            draggable={false}
          />
        </motion.div>

        {/* Headline + supporting text (below the valve) */}
        <motion.div style={{ y: textY }} className="max-w-5xl -mt-6 sm:-mt-10">
          <h1 className="font-heading font-black leading-[0.9] tracking-tighter text-[13vw] sm:text-[10vw] lg:text-[7.5rem]">
            <LineMask delay={1.5}>Stop Leaks</LineMask>
            <LineMask delay={1.75}>
              <span className="text-white/30">Before They </span>
              <span className="text-[#169ED9] text-glow">Start.</span>
            </LineMask>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6"
          >
            <p className="max-w-md text-white/60 text-base leading-relaxed">
              An AI smart valve that automatically detects leaks and shuts off the
              water before any damage can occur.{" "}
              <span className="text-white">Save money. Save water. No property damage.</span>
            </p>
            <div className="flex flex-col sm:flex-row shrink-0 gap-3">
              <a
                href="#pricing"
                data-testid="hero-pricing-btn"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-semibold tracking-wide text-black hover:bg-[#169ED9] transition-colors duration-300"
              >
                See Pricing Options
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
              <a
                href={BOOK_URL}
                data-testid="hero-book-btn"
                className="group inline-flex items-center justify-center gap-3 rounded-full border border-[#169ED9]/40 bg-[#169ED9]/5 px-7 py-4 text-sm font-semibold tracking-wide text-white hover:bg-[#169ED9] hover:text-black transition-colors duration-300"
              >
                Book Free Assessment
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
          Scroll
        </span>
        <div className="h-10 w-px bg-gradient-to-b from-[#169ED9] to-transparent" />
      </motion.div>
    </section>
  );
}
